import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { Switch } from '@/components/form/baseInputs/Switch';
import { Card } from '@/components/layout/Card/Card';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import {
  getEmoneyIntegrationLoginPath,
  getEmoneyIntegrationLogoutPath,
} from '@/modules/assetProviderIntegrations/emoney/utils/emoneyAuthUtils';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { getPublicImageUrl } from '@/utils/staticFiles';

import {
  AdminIntegrationsPage_IntegrationConfigurationFragment,
  useUpdateIntegrationConfigurationMutation,
} from './graphql/AdminIntegrationsPage.generated';
import { IntegrationUserSetupModal } from './IntegrationUserSetupModal';
import { ResyncTenantValuationsButton } from './ResyncTenantValuationsButton/ResyncTenantValuationsButton';

export interface EmoneyIntegrationCardProps {
  isLoggedIn: boolean;
  externalUserName?: string | null;
  configuration: AdminIntegrationsPage_IntegrationConfigurationFragment | null;
  disabled?: boolean;
}

export function EmoneyIntegrationCard({
  isLoggedIn: isIntegrationUserLoggedIn,
  externalUserName,
  configuration,
  disabled = false,
}: EmoneyIntegrationCardProps) {
  const { integrationConfiguration } = useTenantDetailsContext();
  const { createSuccessFeedback, showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [isEmoneyEnabled, setIsEmoneyEnabled] = useState(
    integrationConfiguration?.emoneyEnabled ?? false
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updateConfiguration] = useUpdateIntegrationConfigurationMutation({
    onCompleted: createSuccessFeedback(
      'eMoney configuration updated successfully'
    ),
    onError: (err) => {
      showFeedback('Failed to update eMoney configuration');
      reportError('Failed to update eMoney configuration', err);
    },
  });

  const currentPageURL = getCompletePathFromRouteKey('ADMIN_INTEGRATIONS', {});

  const queryParams = useMemo(
    () => ({
      is_integration_user: 'true',
      redirect: currentPageURL,
    }),
    [currentPageURL]
  );

  async function handleToggleEmoney() {
    if (!configuration) {
      throw new Error('Configuration is not available');
    }

    setIsEmoneyEnabled(!isEmoneyEnabled);
    await updateConfiguration({
      variables: {
        input: {
          id: configuration.id,
          update: {
            emoneyEnabled: !isEmoneyEnabled,
          },
        },
      },
    });
  }

  const getEmoneyButtonProps = useCallback(() => {
    if (isIntegrationUserLoggedIn) {
      return {
        children: 'Disconnect integration user',
        variant: 'secondary' as const,
        href: getEmoneyIntegrationLogoutPath(queryParams),
        method: 'post' as const,
      };
    } else {
      return {
        children: 'Connect integration user',
        variant: 'primary' as const,
        onClick: () => {
          setIsModalOpen(true);
        },
        disabled: !isEmoneyEnabled,
      };
    }
  }, [isEmoneyEnabled, isIntegrationUserLoggedIn, queryParams]);

  const usesIntegrationUser = configuration?.integrationUserEnabled ?? false;
  const canSyncWithAvailableCredentials =
    !usesIntegrationUser || isIntegrationUserLoggedIn;
  const showResyncButton = isEmoneyEnabled && canSyncWithAvailableCredentials;
  const emoneyButtonProps = getEmoneyButtonProps();

  return (
    <Stack position="relative">
      {showResyncButton && (
        <Box position="absolute" right={-5} top={-5}>
          <ResyncTenantValuationsButton />
        </Box>
      )}
      <Box textAlign="center">
        <img
          alt="eMoney"
          src={getPublicImageUrl('/emoneyLogo.png')}
          height={40}
        />
      </Box>
      <Stack mb={2} mt={1} spacing={2}>
        {usesIntegrationUser && (
          <Typography variant="body-s">
            Manage the integration user for your eMoney account. This user
            should have access to all clients and portfolio data to enable
            regular syncing of information between systems.
            <br />
          </Typography>
        )}
        <Card variant="filled" sx={{ p: 2 }}>
          <Switch
            labelPosition="right"
            value={isEmoneyEnabled}
            label="Enable eMoney"
            onChange={() => handleToggleEmoney()}
            disabled={disabled}
          />
        </Card>
      </Stack>
      {usesIntegrationUser && (
        <Box mt={2} mb={2} textAlign="center">
          <Button
            size="sm"
            target="_self"
            {...emoneyButtonProps}
            fullWidth
            external={isIntegrationUserLoggedIn} // Only external if it's the disconnect button
            disabled={disabled || emoneyButtonProps.disabled}
          />
        </Box>
      )}

      {usesIntegrationUser && isIntegrationUserLoggedIn && externalUserName && (
        <Callout severity="info-low">Linked using {externalUserName}</Callout>
      )}

      <IntegrationUserSetupModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onContinue={() => {
          setIsModalOpen(false);
          window.location.href = getEmoneyIntegrationLoginPath(queryParams);
        }}
        providerName="eMoney"
      />
    </Stack>
  );
}
